import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthProvider";
import {
    type Agendamento,
    type AgendamentoHistoricoItem,
    AgendamentoService,
} from "@/services/app/agendamento";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeft,
    Calendar,
    Cat,
    Clock,
    Dog,
    ExternalLink,
    Loader2,
    RefreshCw,
    Settings,
    User
} from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type AgendamentoDetalheProps = {
  agendamento: Agendamento;
  historico?: AgendamentoHistoricoItem[];
  onClose: () => void;
  isLoadingHistorico: boolean;
  onGoToAgendamentos: () => void;
};

const AgendamentoDetalhe: React.FC<AgendamentoDetalheProps> = ({
  agendamento,
  historico,
  onClose,
  isLoadingHistorico,
  onGoToAgendamentos,
}) => {
  const { user } = useAuth();

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "PENDENTE":
        return "#FF9800";
      case "AGENDADA":
        return "#4CAF50";
      case "CANCELADA":
        return "#F44336";
      case "REALIZADA":
        return "#2196F3";
      default:
        return "#9E9E9E";
    }
  };

  const getStatusLabel = (status: string): string | null => {
    if (!status) return null;
    return status.charAt(0).toUpperCase() + status.toLowerCase().slice(1);
  };

  // Agrupa as alterações por data
  const groupedHistorico = useMemo(() => {
    if (!historico) return {};

    const grouped: Record<string, AgendamentoHistoricoItem[]> = {};

    historico.forEach((item) => {
      const date = new Date(item.createdAt);
      const dateKey = moment(date).format("DD/MM/YYYY");

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }

      grouped[dateKey].push(item);
    });

    return grouped;
  }, [historico]);

  // Cria um array ordenado de datas
  const dateKeys = useMemo(() => {
    return Object.keys(groupedHistorico).sort((a, b) => {
      const dateA = new Date(a.split("/").reverse().join("-"));
      const dateB = new Date(b.split("/").reverse().join("-"));
      return dateB.getTime() - dateA.getTime(); // Ordenação decrescente
    });
  }, [groupedHistorico]);

  const renderHistoricoItem = (item: AgendamentoHistoricoItem) => {
    const time = new Date(item.createdAt).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const isSystem = !item.byTUTOR;

    return (
      <div key={item.id} className="mb-3 px-1">
        <div className="flex items-start">
          <div
            className={`h-8 w-8 rounded-full flex items-center justify-center mr-2 ${
              isSystem ? "bg-blue-100" : "bg-green-100"
            }`}
          >
            {isSystem ? (
              <Settings className="h-4 w-4 text-blue-600" />
            ) : (
              <User className="h-4 w-4 text-green-600" />
            )}
          </div>

          <div className="flex-1 bg-white rounded-lg p-3 border border-gray-100">
            <div className="flex justify-between items-center mb-1">
              <span className="font-medium text-gray-800 capitalize">
                {isSystem ? "Sistema" : user?.nome || "Você"}
              </span>
              <span className="text-xs text-gray-500">{time}</span>
            </div>

            <p className="text-gray-700 mb-2">{item.descricao}</p>

            {item.status && (
              <div className="flex justify-end">
                <div
                  className="px-2 py-1 rounded-full"
                  style={{
                    backgroundColor: getStatusColor(item.status) + "20",
                  }}
                >
                  <span
                    style={{
                      color: getStatusColor(item.status),
                      fontWeight: "500",
                      fontSize: "11px",
                    }}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDateSection = (dateKey: string) => {
    return (
      <div key={dateKey} className="mb-4">
        <div className="flex items-center mb-2">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="mx-2 text-xs font-medium text-gray-500">
            {dateKey}
          </span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {groupedHistorico[dateKey].map((histItem) =>
          renderHistoricoItem(histItem)
        )}
      </div>
    );
  };

  return (
    <Sheet open={true} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <SheetContent
        className="w-full sm:max-w-7xl mx-auto pt-safe h-[85vh] overflow-hidden flex flex-col p-0"
        side="bottom"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center mr-3"
                  style={{
                    backgroundColor: getStatusColor(agendamento.status) + "20",
                  }}
                >
                  <Calendar
                    className="h-5 w-5"
                    style={{ color: getStatusColor(agendamento.status) }}
                  />
                </div>
                <div>
                  <SheetTitle className="text-lg font-bold text-gray-800 text-start">
                    {agendamento.pet.nome}
                  </SheetTitle>
                  <div className="flex items-center">
                    <div
                      className="h-2 w-2 rounded-full mr-1"
                      style={{
                        backgroundColor: getStatusColor(agendamento.status),
                      }}
                    />
                    <SheetDescription className="text-sm text-gray-600">
                      {getStatusLabel(agendamento.status)} • {agendamento.tipo.nome}
                    </SheetDescription>
                  </div>
                </div>
              </div>

              <button
                onClick={onGoToAgendamentos}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                title="Ver em Agendamentos"
              >
                <ExternalLink className="h-4 w-4 text-blue-600" />
              </button>
            </div>
          </SheetHeader>
        </div>

        {/* Timeline/Histórico */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {isLoadingHistorico ? (
            <div className="flex flex-col items-center mt-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="text-gray-500 mt-3">
                Carregando histórico...
              </span>
            </div>
          ) : (
            <>
              {historico && historico.length > 0 ? (
                <div className="space-y-4">
                  {dateKeys.map((dateKey) => renderDateSection(dateKey))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center mt-8">
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Clock className="h-6 w-6 text-gray-400" />
                  </div>
                  <span className="text-gray-500 text-base mb-1">
                    Sem atualizações
                  </span>
                  <p className="text-gray-400 text-sm text-center px-8">
                    Ainda não há histórico de alterações para este agendamento
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function HistoricoAgendamentos() {
  const [agendamentoSelecionado, setAgendamentoSelecionado] =
    useState<Agendamento | null>(null);
  const [historicoVisible, setHistoricoVisible] = useState(false);
  const processedParamIdRef = useRef<string | null>(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const agendamentoService = new AgendamentoService();

  const {
    data: agendamentos,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["agendamentos", user?.id],
    queryFn: async () => {
      const agendamentosData = await agendamentoService.getAgendamentos();
      return agendamentosData.sort((a, b) => {
        type StatusType = "AGENDADA" | "PENDENTE" | "CANCELADA" | "REALIZADA";
        const statusPriority: Record<StatusType, number> = {
          AGENDADA: 0,
          PENDENTE: 1,
          CANCELADA: 3,
          REALIZADA: 2,
        };

        if (
          statusPriority[a.status as StatusType] !==
          statusPriority[b.status as StatusType]
        ) {
          return (
            statusPriority[a.status as StatusType] -
            statusPriority[b.status as StatusType]
          );
        }

        if (a.data && b.data) {
          return new Date(a.data).getTime() - new Date(b.data).getTime();
        }
        return 0;
      });
    },
    enabled: !!user?.id,
  });

  // Verificar se há parâmetro de detalhes na URL
  const detalhesId = searchParams.get("detalhes");

  // Efeito para abrir modal de detalhes automaticamente
  useEffect(() => {
    if (detalhesId && agendamentos && !historicoVisible && detalhesId !== processedParamIdRef.current) {
      const agendamento = agendamentos.find((item) => item.id === detalhesId);
      if (agendamento) {
        setAgendamentoSelecionado(agendamento);
        setHistoricoVisible(true);
        processedParamIdRef.current = detalhesId;
        // Limpar o parâmetro da URL
        setSearchParams({});
      }
    }
  }, [detalhesId, agendamentos, historicoVisible, setSearchParams]);

  const { data: historico, isLoading: isLoadingHistorico } = useQuery({
    queryKey: ["agendamento-historico", agendamentoSelecionado?.id],
    queryFn: async () => {
      if (!agendamentoSelecionado?.id) return null;
      return await agendamentoService.getAgendamentoHistorico(
        agendamentoSelecionado.id
      );
    },
    enabled: !!agendamentoSelecionado?.id,
  });

  const handleVisualizarAgendamento = (agendamento: Agendamento) => {
    if (agendamento) {
      setAgendamentoSelecionado(agendamento);
      setHistoricoVisible(true);
    }
  };

  const handleFecharDetalhe = () => {
    setHistoricoVisible(false);
    setAgendamentoSelecionado(null);
    processedParamIdRef.current = null;
    // Limpar parâmetros da URL se existirem
    if (searchParams.get("detalhes")) {
      setSearchParams({});
    }
  };

  const handleGoToAgendamentos = () => {
    if (agendamentoSelecionado) {
      navigate(`/agendamentos?detalhes=${agendamentoSelecionado.id}`);
    } else {
      setSearchParams({});
      
      navigate("/agendamentos");
    }
  };

  const getStatusLabel = (status: string): string => {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.toLowerCase().slice(1);
  };

  const getEspecieLabel = (especie: string): string => {
    return especie === "CACHORRO" ? "Cão" : "Gato";
  };

  const renderAgendamentoItem = (item: Agendamento) => (
    <div
      key={item.id}
      onClick={() => handleVisualizarAgendamento(item)}
      className="bg-white rounded-xl p-4 mb-4 border border-gray-100 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div
            className={`rounded-full p-3 mr-3 ${
              item.pet.especie === "CACHORRO" ? "bg-blue-100" : "bg-red-100"
            }`}
          >
            {item.pet.especie === "CACHORRO" ? (
              <Dog className="h-6 w-6 text-blue-600" />
            ) : (
              <Cat className="h-6 w-6 text-red-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              {item.pet.nome}
            </h3>
            <p className="text-gray-500">
              {getEspecieLabel(item.pet.especie)}
            </p>
            <div className="flex items-center mt-1">
              <div
                className={`h-2 w-2 rounded-full mr-2 ${
                  item.status === "PENDENTE"
                    ? "bg-yellow-500"
                    : item.status === "AGENDADA"
                    ? "bg-blue-600"
                    : item.status === "REALIZADA"
                    ? "bg-green-600"
                    : "bg-red-500"
                }`}
              />
              <span
                className={`font-medium ${
                  item.status === "PENDENTE"
                    ? "text-yellow-500"
                    : item.status === "AGENDADA"
                    ? "text-blue-600"
                    : item.status === "REALIZADA"
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {getStatusLabel(item.status)}
              </span>
            </div>
          </div>
        </div>
        <div className="text-gray-400">
          <Clock className="h-5 w-5" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header>
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Histórico
                </h1>
                <p className="text-sm text-gray-500">
                  Visualize seus agendamentos
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <RefreshCw
                className={`h-5 w-5 text-gray-600 ${
                  isRefetching ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-2 pb-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center mt-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="text-gray-500 mt-3">Carregando agendamentos...</span>
          </div>
        ) : (
          <>
            {agendamentos && agendamentos.length > 0 ? (
              <div className="space-y-2 mt-4">
                {agendamentos.map((agendamento) =>
                  renderAgendamentoItem(agendamento)
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center mt-20">
                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Calendar className="h-8 w-8 text-gray-400" />
                </div>
                <span className="text-gray-500 text-lg mb-1">
                  Nenhum agendamento encontrado
                </span>
                <p className="text-gray-400 text-sm text-center">
                  Você ainda não possui agendamentos
                </p>
              </div>
            )}
          </>
        )}

        {agendamentoSelecionado && historicoVisible && (
          <AgendamentoDetalhe
            agendamento={agendamentoSelecionado}
            historico={historico || []}
            onClose={handleFecharDetalhe}
            isLoadingHistorico={isLoadingHistorico}
            onGoToAgendamentos={handleGoToAgendamentos}
          />
        )}
      </div>
    </div>
  );
}